import React, { useEffect, useState, useContext, useRef, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { fetchPatentFullText, checkLogin } from './api'
import ThemeContext from './user/ThemeContext'
import Loader from './component/Loader'
import { removeCountryAndKindCodes } from './util/patent'
import PatentLandingPage from './PatentLandingPage'
import logoImage from '@/assets/logo-beta.png'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Settings, User, LogOut } from 'lucide-react'

type HighlightDirection = 1 | -1
type HighlightMatch = { start: number; end: number; type: 'search' | 'claims'; text: string }
type HighlightRange = { start: number; end: number; type: 'search' | 'claims'; text: string }

interface PatentData {
  patent_number?: string
  title?: string
  abstract?: string
  description?: string
  claims?: string
  drawings?: string[]
  inventors?: string[]
  assignee?: string
  filing_date?: string
  publication_date?: string
  grant_date?: string
  [key: string]: unknown
}

const PatentFullTextDisplay = () => {
  const { docNumber: patentNumber } = useParams<{ docNumber: string }>();
  const navigate = useNavigate()
  const { theme } = useContext(ThemeContext)
  const [patentData, setPatentData] = useState<PatentData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [referenceNumeral, setReferenceNumeral] = useState('')
  const [claimsPinned, setClaimsPinned] = useState(true)
  const [drawingsPinned, setDrawingsPinned] = useState(true)
  const [searchMatchCount, setSearchMatchCount] = useState(0)
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0)
  const [filterSearchResults, setFilterSearchResults] = useState(false)
  const [referenceNumeralMatchCount, setReferenceNumeralMatchCount] = useState(0)
  const [currentReferenceNumeralIndex, setCurrentReferenceNumeralIndex] = useState(0)
  const [filterReferenceNumeralResults, setFilterReferenceNumeralResults] = useState(false)
  const [claimsFilterText, setClaimsFilterText] = useState('')
  const [claimsFilterEnabled, setClaimsFilterEnabled] = useState(false)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [username, setUsername] = useState('')
  const [showShareTooltip, setShowShareTooltip] = useState(false)
  const [selectedDrawingIndex, setSelectedDrawingIndex] = useState<number | null>(null)
  const [drawingViewerWidth, setDrawingViewerWidth] = useState(320) // 320px = w-80
  const [isResizingDrawingWidth, setIsResizingDrawingWidth] = useState(false)
  const [drawingViewerWidthBeforeExpand, setDrawingViewerWidthBeforeExpand] = useState(320)
  const [hasInitializedDrawing, setHasInitializedDrawing] = useState(false)
  const [showPatentSelector, setShowPatentSelector] = useState(false)
  const [newPatentNumber, setNewPatentNumber] = useState('')
  const [copiedTitle, setCopiedTitle] = useState(false)
  const [copiedAbstract, setCopiedAbstract] = useState(false)
  const [copiedClaims, setCopiedClaims] = useState(false)
  const [copiedDescription, setCopiedDescription] = useState(false)
  const [drawingRotation, setDrawingRotation] = useState(0)
  const [drawingImageRef, setDrawingImageRef] = useState<HTMLImageElement | null>(null)
  const [drawingZoom, setDrawingZoom] = useState(100)
  const [showExportModal, setShowExportModal] = useState(false)
  const [exportFormat, setExportFormat] = useState('option1') // option1 or option2
  const [includeAbstract, setIncludeAbstract] = useState(true)
  const [includeDescription, setIncludeDescription] = useState(true)
  const [includeClaims, setIncludeClaims] = useState(true)
  const [includeMetadata, setIncludeMetadata] = useState(true)

  // Debounced state for highlighting (prevents slow processing while typing)
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [debouncedReferenceNumeral, setDebouncedReferenceNumeral] = useState('')
  const [debouncedClaimsFilterText, setDebouncedClaimsFilterText] = useState('')

  const descriptionRef = useRef<HTMLDivElement | null>(null)
  const claimsSidebarRef = useRef<HTMLDivElement | null>(null)
  const drawingViewerRef = useRef<HTMLDivElement | null>(null)

  // Helper function to normalize patent number (uppercase country code and kind code)
  const normalizePatentNumber = (number: string) => {
    const cleaned = number.trim().replace(/[\s,\-]/g, '')
    return cleaned.replace(/^([a-z]{2})(\d+)([a-z]\d*)?$/i, (_match, country: string, num: string, kind?: string) => {
      return country.toUpperCase() + num + (kind ? kind.toUpperCase() : '')
    })
  }

  const splitClaimsText = (claimsText: string): string[] => {
    return claimsText.split(/\n(?=\d+\.\s)/g).filter((claim: string) => claim.trim())
  }

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('id_token')
    localStorage.removeItem('username')
    localStorage.removeItem('email')
    localStorage.removeItem('account_type')
    setIsLoggedIn(false)
    setUsername('')
    navigate('/')
  }

  // Check if user is logged in
  useEffect(() => {
    checkLogin()
      .then(response => {
        if (response && response.username) {
          setIsLoggedIn(true)
          setUsername(response.username)
        }
      })
      .catch(() => {
        setIsLoggedIn(false)
      })
  }, [])

  useEffect(() => {
    const loadPatentData = async () => {
      try {
        setLoading(true)
        setError(null)

        // Check if patent number is just digits (no country code) and redirect to add "US"
        if (patentNumber && /^\d+$/.test(patentNumber)) {
          navigate(`/US${patentNumber}`, { replace: true })
          return
        }

        const data = await fetchPatentFullText(patentNumber)
        setPatentData(data)
      } catch (err) {
        console.error('Error loading patent:', err)
        const message = err instanceof Error ? err.message : 'Failed to load patent data'
        setError(message)
      } finally {
        setLoading(false)
      }
    }

    if (patentNumber) {
      loadPatentData()
    }
  }, [patentNumber, navigate])

  // Handle drawing viewer width resize
  useEffect(() => {
    if (!isResizingDrawingWidth) return

    const handleMouseMove = (e: MouseEvent) => {
      if (!drawingViewerRef.current) return

      const containerRect = drawingViewerRef.current.getBoundingClientRect()
      const newWidth = Math.max(200, Math.min(800, containerRect.right - e.clientX))
      setDrawingViewerWidth(newWidth)
    }

    const handleMouseUp = () => {
      setIsResizingDrawingWidth(false)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizingDrawingWidth])

  // Adjust viewer width and zoom when drawing is rotated or selected/deselected
  useEffect(() => {
    if (selectedDrawingIndex !== null && drawingImageRef) {
      // Save the current width before expanding
      if (!drawingViewerWidthBeforeExpand || drawingViewerWidthBeforeExpand === 320) {
        setDrawingViewerWidthBeforeExpand(drawingViewerWidth)
      }

      const img = drawingImageRef
      const imgWidth = img.naturalWidth
      const imgHeight = img.naturalHeight

      if (imgWidth && imgHeight) {
        // Calculate the required width based on rotation
        let requiredWidth
        const maxViewportWidth = window.innerWidth * 0.6 // Max 60% of screen
        const minViewportWidth = window.innerWidth * 0.4 // Min 40% of screen
        const availableHeight = window.innerHeight - 300 // Account for headers/padding

        if (drawingRotation % 180 !== 0) {
          // When rotated 90° or 270°, auto-zoom to 50%
          setDrawingZoom(50)

          // After rotation:
          // - The image's width of imgWidth becomes the displayed HEIGHT
          // - The image's height of imgHeight becomes the displayed WIDTH
          //
          // We want to fit the rotated HEIGHT (original width) into availableHeight
          // So scale factor = availableHeight / imgWidth
          //
          // At that scale, the rotated WIDTH would be: imgHeight * (availableHeight / imgWidth)
          // Then we apply 50% zoom, so multiply by 0.5

          const scaleToFitHeight = availableHeight / imgWidth
          const widthAtFullZoom = imgHeight * scaleToFitHeight
          const widthAt50Zoom = widthAtFullZoom * 0.5

          requiredWidth = widthAt50Zoom + 80 // Add padding
          requiredWidth = Math.max(requiredWidth, minViewportWidth)
          requiredWidth = Math.min(requiredWidth, maxViewportWidth)
        } else {
          // Normal orientation (0° or 180°) - set zoom to 75% and fit accordingly
          setDrawingZoom(75)

          // The image displays at 100% of container width
          // At 75% zoom, we want the viewer to be sized so the image at 75% fits nicely
          // If we set viewer width to minViewportWidth, the image fills it at 100% zoom
          // At 75% zoom, we need a smaller viewer width

          // Start with a reasonable default width for normal orientation
          requiredWidth = minViewportWidth
        }

        setDrawingViewerWidth(Math.max(requiredWidth, minViewportWidth))
        setHasInitializedDrawing(true)
      } else {
        // Fallback if image dimensions not available yet
        const minWidth = window.innerWidth * 0.4
        setDrawingViewerWidth(minWidth)
        // Set initial zoom to 75% even before image loads
        if (!hasInitializedDrawing) {
          setDrawingZoom(75)
        }
      }
    } else if (selectedDrawingIndex === null) {
      // When returning to thumbnail view, restore original width if it was saved
      if (drawingViewerWidthBeforeExpand && drawingViewerWidthBeforeExpand >= 320) {
        setDrawingViewerWidth(drawingViewerWidthBeforeExpand)
        setDrawingViewerWidthBeforeExpand(320)
      } else {
        setDrawingViewerWidth(320)
      }
      // Reset zoom when closing
      setDrawingZoom(100)
      setHasInitializedDrawing(false)
    }
  }, [selectedDrawingIndex, drawingRotation, drawingImageRef])

  // Debounce search query (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery])

  // Debounce reference numeral (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedReferenceNumeral(referenceNumeral)
    }, 300)

    return () => clearTimeout(timer)
  }, [referenceNumeral])

  // Debounce claims filter text (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedClaimsFilterText(claimsFilterText)
    }, 300)

    return () => clearTimeout(timer)
  }, [claimsFilterText])

  // Listen for mouseup in claims sidebar to capture completed selections
  useEffect(() => {
    const handleMouseUp = (_event: MouseEvent) => {
      // Small delay to ensure selection is finalized
      setTimeout(() => {
        const selection = window.getSelection()
        const selectedText = selection?.toString().trim()

        // Check if the selection is within the claims sidebar
        if (selectedText && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0)
          const container = range.commonAncestorContainer

          if (claimsSidebarRef.current && container instanceof Node && claimsSidebarRef.current.contains(container)) {
            setClaimsFilterText(selectedText)

            // Clear selection after a brief moment to prevent it from expanding
            setTimeout(() => {
              if (window.getSelection) {
                window.getSelection().removeAllRanges()
              }
            }, 50)
          }
        }
      }, 10)
    }

    // Add listener to document so it works even if ref changes
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [])

  // Filter description paragraphs based on reference numeral, search text, or claims filter
  const filterDescriptionByNumeral = (htmlContent: string) => {
    // Use debounced values to match the highlighting logic
    const hasNumeralFilter = filterReferenceNumeralResults && debouncedReferenceNumeral.trim()
    const hasSearchFilter = filterSearchResults && debouncedSearchQuery.trim()
    const hasClaimsFilter = claimsFilterEnabled && debouncedClaimsFilterText.trim()

    // If search text matches claims filter text, ignore claims filter to avoid conflicts
    const searchMatchesClaimsFilter = debouncedSearchQuery.trim().toLowerCase() === debouncedClaimsFilterText.trim().toLowerCase()
    const effectiveClaimsFilter = hasClaimsFilter && !searchMatchesClaimsFilter

    if (!hasNumeralFilter && !hasSearchFilter && !effectiveClaimsFilter) return htmlContent

    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlContent, 'text/html')

    const allElements = Array.from(doc.body.querySelectorAll<HTMLElement>('p, div'))

    allElements.forEach(elem => {
      let shouldShow = false

      if (hasNumeralFilter) {
        const strongTags = elem.querySelectorAll<HTMLElement>('strong')
        strongTags.forEach(strong => {
          if (strong.textContent?.trim() === debouncedReferenceNumeral.trim()) {
            shouldShow = true
            // Highlight the matching reference numeral
            strong.classList.add('bg-blue-300', 'text-black', 'px-1', 'rounded')
          }
        })
      }

      // Check for search matches and claims filter matches
      // Note: When both filters have the same text, highlighting may create only one mark type
      // So we check for ANY highlighted text (mark tags) if both filters are looking for the same thing
      if (!shouldShow && (hasSearchFilter || effectiveClaimsFilter)) {
        const searchMarks = hasSearchFilter ? elem.querySelectorAll('.search-match') : []
        const claimsMarks = effectiveClaimsFilter ? elem.querySelectorAll('.claims-filter-match') : []

        // Show if either filter found matches
        if (searchMarks.length > 0 || claimsMarks.length > 0) {
          shouldShow = true
        }
      }

      const isHeading = elem.classList.contains('font-bold')

      if (isHeading) {
        let nextElem = elem.nextElementSibling as HTMLElement | null
        let foundInSection = false
        while (nextElem && !nextElem.classList.contains('font-bold')) {
          if (hasNumeralFilter) {
            const strongsInNext = nextElem.querySelectorAll<HTMLElement>('strong')
            strongsInNext.forEach(strong => {
              if (strong.textContent?.trim() === debouncedReferenceNumeral.trim()) {
                foundInSection = true
                // Highlight matching numerals in section
                strong.classList.add('bg-blue-300', 'text-black', 'px-1', 'rounded')
              }
            })
          }
          // Check for search and claims filter matches in section
          if (!foundInSection && (hasSearchFilter || effectiveClaimsFilter)) {
            const searchMarksInNext = hasSearchFilter ? nextElem.querySelectorAll('.search-match') : []
            const claimsMarksInNext = effectiveClaimsFilter ? nextElem.querySelectorAll('.claims-filter-match') : []

            if (searchMarksInNext.length > 0 || claimsMarksInNext.length > 0) {
              foundInSection = true
            }
          }
          nextElem = nextElem.nextElementSibling as HTMLElement | null
        }
        if (!foundInSection) {
          elem.style.display = 'none'
        }
      } else if (!shouldShow) {
        elem.style.display = 'none'
      }
    })

    return doc.body.innerHTML
  }

  // Memoize processed description content
  const processedDescription = useMemo(() => {
    if (!patentData?.description) return ''

    const highlightSearchQuery = (htmlContent: string) => {
      const query = debouncedSearchQuery.trim()
      const claimsFilter = debouncedClaimsFilterText.trim() // Always highlight if text exists
      const refNumeral = debouncedReferenceNumeral.trim()

      if (!query && !claimsFilter && !refNumeral) {
        return { html: htmlContent, searchCount: 0, numeralCount: 0 }
      }

      const parser = new DOMParser()
      const doc = parser.parseFromString(htmlContent, 'text/html')
      let searchCount = 0
      let numeralCount = 0

      const highlightTextNodes = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent
          const lowerText = text.toLowerCase()

          // Check if we have both query and claims filter
          const hasQuery = query && lowerText.includes(query.toLowerCase())
          const hasClaimsFilter = claimsFilter && lowerText.includes(claimsFilter.toLowerCase())

          if (!hasQuery && !hasClaimsFilter) {
            return // No highlighting needed
          }

          // If we have both, we need to handle them together
          if (hasQuery && hasClaimsFilter) {
            // Build a list of all matches with their positions
            const matches: HighlightMatch[] = []
            const lowerQuery = query.toLowerCase()
            const lowerClaimsFilter = claimsFilter.toLowerCase()

            // Find all query matches
            let pos = 0
            while ((pos = lowerText.indexOf(lowerQuery, pos)) !== -1) {
            matches.push({ start: pos, end: pos + query.length, type: 'search', text: text.substring(pos, pos + query.length) })
              pos++
            }

            // Find all claims filter matches
            pos = 0
            while ((pos = lowerText.indexOf(lowerClaimsFilter, pos)) !== -1) {
              matches.push({ start: pos, end: pos + claimsFilter.length, type: 'claims', text: text.substring(pos, pos + claimsFilter.length) })
              pos++
            }

            // Sort by length (longer first), then by start position - this ensures longer matches take priority
            matches.sort((a, b) => {
              const lenDiff = (b.end - b.start) - (a.end - a.start)
              if (lenDiff !== 0) return lenDiff
              return a.start - b.start
            })

            // Build the highlighted content, handling overlaps by keeping longer matches
            const highlightParts: Node[] = []
            let lastEnd = 0
            const usedRanges: HighlightRange[] = []

            matches.forEach(match => {
              // Check if this match overlaps with any already used range
              const overlaps = usedRanges.some(range =>
                (match.start >= range.start && match.start < range.end) ||
                (match.end > range.start && match.end <= range.end) ||
                (match.start <= range.start && match.end >= range.end)
              )

              if (!overlaps) {
                usedRanges.push({ start: match.start, end: match.end, type: match.type, text: match.text })
              }
            })

            // Sort used ranges by position
            usedRanges.sort((a, b) => a.start - b.start)

            // Now build the final highlighted text using the non-overlapping ranges
            usedRanges.forEach(range => {
              // Add text before this match
              if (range.start > lastEnd) {
                highlightParts.push(document.createTextNode(text.substring(lastEnd, range.start)))
              }

              if (range.type === 'search') {
                searchCount++
                const mark = document.createElement('mark')
                mark.className = 'bg-yellow-300 text-black search-match'
                mark.setAttribute('data-match-index', (searchCount - 1).toString())
                mark.textContent = range.text
                highlightParts.push(mark)
              } else {
                const mark = document.createElement('mark')
                mark.className = 'bg-green-300 text-black claims-filter-match'
                mark.textContent = range.text
                highlightParts.push(mark)
              }
              lastEnd = range.end
            })

            // Add remaining text
            if (lastEnd < text.length) {
              highlightParts.push(document.createTextNode(text.substring(lastEnd)))
            }

            const span = document.createElement('span')
            highlightParts.forEach(part => span.appendChild(part))
            node.parentNode?.replaceChild(span, node)
          } else if (hasQuery) {
            // Only search query
            const lowerQuery = query.toLowerCase()
            const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'))
            const highlightParts: Node[] = []
            parts.forEach((part: string) => {
              if (part.toLowerCase() === lowerQuery) {
                searchCount++
                const mark = document.createElement('mark')
                mark.className = 'bg-yellow-300 text-black search-match'
                mark.setAttribute('data-match-index', (searchCount - 1).toString())
                mark.textContent = part
                highlightParts.push(mark)
              } else if (part) {
                highlightParts.push(document.createTextNode(part))
              }
            })
            const span = document.createElement('span')
            highlightParts.forEach(part => span.appendChild(part))
            node.parentNode?.replaceChild(span, node)
          } else if (hasClaimsFilter) {
            // Only claims filter
            const lowerClaimsFilter = claimsFilter.toLowerCase()
            const parts = text.split(new RegExp(`(${claimsFilter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'))
            const highlightParts: Node[] = []
            parts.forEach((part: string) => {
              if (part.toLowerCase() === lowerClaimsFilter) {
                const mark = document.createElement('mark')
                mark.className = 'bg-green-300 text-black claims-filter-match'
                mark.textContent = part
                highlightParts.push(mark)
              } else if (part) {
                highlightParts.push(document.createTextNode(part))
              }
            })
            const span = document.createElement('span')
            highlightParts.forEach(part => span.appendChild(part))
            node.parentNode?.replaceChild(span, node)
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as HTMLElement
          if (refNumeral && element.tagName === 'STRONG') {
            const strongText = element.textContent?.trim()
            if (strongText === refNumeral) {
              numeralCount++
              element.classList.add('bg-blue-300', 'text-black', 'px-1', 'rounded', 'reference-numeral-match')
              element.setAttribute('data-numeral-index', (numeralCount - 1).toString())
            }
          }

          if (element.tagName !== 'MARK') {
            Array.from(element.childNodes).forEach(child => highlightTextNodes(child))
          }
        }
      }

      highlightTextNodes(doc.body)
      return { html: doc.body.innerHTML, searchCount, numeralCount }
    }

    const highlighted = highlightSearchQuery(patentData.description)
    const filtered = filterDescriptionByNumeral(highlighted.html)

    // Update search match count and jump to first result
    if (highlighted.searchCount !== searchMatchCount) {
      setTimeout(() => {
        setSearchMatchCount(highlighted.searchCount)
        setCurrentMatchIndex(highlighted.searchCount > 0 ? 0 : 0)

        // Jump to first search match if results exist
        if (highlighted.searchCount > 0) {
          setTimeout(() => {
            const matches = descriptionRef.current?.querySelectorAll<HTMLElement>('.search-match')
            if (matches && matches[0]) {
              matches[0].scrollIntoView({ behavior: 'smooth', block: 'center' })
              matches.forEach((m, i) => {
                if (i === 0) {
                  m.classList.add('ring-2', 'ring-blue-500')
                } else {
                  m.classList.remove('ring-2', 'ring-blue-500')
                }
              })
            }
          }, 100)
        }
      }, 0)
    }

    // Update reference numeral match count and jump to first result
    if (highlighted.numeralCount !== referenceNumeralMatchCount) {
      setTimeout(() => {
        setReferenceNumeralMatchCount(highlighted.numeralCount)
        setCurrentReferenceNumeralIndex(highlighted.numeralCount > 0 ? 0 : 0)

        // Jump to first reference numeral match if results exist
        if (highlighted.numeralCount > 0) {
          setTimeout(() => {
            const matches = descriptionRef.current?.querySelectorAll<HTMLElement>('.reference-numeral-match')
            if (matches && matches[0]) {
              matches[0].scrollIntoView({ behavior: 'smooth', block: 'center' })
              matches.forEach((m, i) => {
                if (i === 0) {
                  m.classList.add('ring-2', 'ring-purple-500')
                } else {
                  m.classList.remove('ring-2', 'ring-purple-500')
                }
              })
            }
          }, 100)
        }
      }, 0)
    }

    return filtered
  }, [patentData?.description, debouncedSearchQuery, debouncedReferenceNumeral, filterSearchResults, filterReferenceNumeralResults, debouncedClaimsFilterText, claimsFilterEnabled, searchMatchCount, referenceNumeralMatchCount])

  // Memoize processed abstract content
  const processedAbstract = useMemo(() => {
    if (!patentData?.abstract) return patentData?.abstract || ''

    const query = debouncedSearchQuery.trim()
    const claimsFilter = debouncedClaimsFilterText.trim()
    const refNumeral = debouncedReferenceNumeral.trim()

    // If no highlighting needed, return plain text
    if (!query && !claimsFilter && !refNumeral) {
      return patentData.abstract
    }

    let result = patentData.abstract
    const lowerText = result.toLowerCase()

    // Check what needs to be highlighted
    const hasQuery = query && lowerText.includes(query.toLowerCase())
    const hasClaimsFilter = claimsFilter && lowerText.includes(claimsFilter.toLowerCase())
    const hasRefNumeral = refNumeral && result.includes(refNumeral)

    // Handle overlapping highlights (search query + claims filter)
    if (hasQuery && hasClaimsFilter) {
      const matches: HighlightMatch[] = []
      const lowerQuery = query.toLowerCase()
      const lowerClaimsFilter = claimsFilter.toLowerCase()

      // Find all query matches
      let pos = 0
      while ((pos = lowerText.indexOf(lowerQuery, pos)) !== -1) {
        matches.push({ start: pos, end: pos + query.length, type: 'search', text: result.substring(pos, pos + query.length) })
        pos++
      }

      // Find all claims filter matches
      pos = 0
      while ((pos = lowerText.indexOf(lowerClaimsFilter, pos)) !== -1) {
        matches.push({ start: pos, end: pos + claimsFilter.length, type: 'claims', text: result.substring(pos, pos + claimsFilter.length) })
        pos++
      }

      // Sort by length (longer first), then by start position
      matches.sort((a, b) => {
        const lenDiff = (b.end - b.start) - (a.end - a.start)
        if (lenDiff !== 0) return lenDiff
        return a.start - b.start
      })

      // Remove overlapping matches, keeping longer ones
      const usedRanges: HighlightRange[] = []
      matches.forEach(match => {
        const overlaps = usedRanges.some(range =>
          (match.start >= range.start && match.start < range.end) ||
          (match.end > range.start && match.end <= range.end) ||
          (match.start <= range.start && match.end >= range.end)
        )
        if (!overlaps) {
          usedRanges.push({ start: match.start, end: match.end, type: match.type, text: match.text })
        }
      })

      // Sort by position
      usedRanges.sort((a, b) => a.start - b.start)

      // Build highlighted text
      let highlighted = ''
      let lastEnd = 0
      usedRanges.forEach(range => {
        highlighted += result.substring(lastEnd, range.start)
        if (range.type === 'search') {
          highlighted += `<mark class="bg-yellow-300 text-black search-match">${range.text}</mark>`
        } else {
          highlighted += `<mark class="bg-green-300 text-black claims-filter-match">${range.text}</mark>`
        }
        lastEnd = range.end
      })
      highlighted += result.substring(lastEnd)
      result = highlighted
    } else if (hasQuery) {
      // Only search query
      const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
      result = result.replace(regex, '<mark class="bg-yellow-300 text-black search-match">$1</mark>')
    } else if (hasClaimsFilter) {
      // Only claims filter
      const regex = new RegExp(`(${claimsFilter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
      result = result.replace(regex, '<mark class="bg-green-300 text-black claims-filter-match">$1</mark>')
    }

    // Highlight reference numerals (simple text matching for abstract)
    if (hasRefNumeral) {
      const regex = new RegExp(`\\b(${refNumeral.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})\\b`, 'g')
      result = result.replace(regex, '<mark class="bg-blue-300 text-black reference-numeral-match">$1</mark>')
    }

    return result
  }, [patentData?.abstract, debouncedSearchQuery, debouncedReferenceNumeral, debouncedClaimsFilterText])

  // Navigate to next/previous match
  const navigateToMatch = (direction: HighlightDirection) => {
    if (searchMatchCount === 0) return

    let newIndex = currentMatchIndex + direction
    if (newIndex < 0) newIndex = searchMatchCount - 1
    if (newIndex >= searchMatchCount) newIndex = 0

    setCurrentMatchIndex(newIndex)

    setTimeout(() => {
      const matches = descriptionRef.current?.querySelectorAll<HTMLElement>('.search-match')
      if (matches && matches[newIndex]) {
        matches[newIndex].scrollIntoView({ behavior: 'smooth', block: 'center' })
        matches.forEach((m, i) => {
          if (i === newIndex) {
            m.classList.add('ring-2', 'ring-blue-500')
          } else {
            m.classList.remove('ring-2', 'ring-blue-500')
          }
        })
      }
    }, 100)
  }

  // Navigate to next/previous reference numeral
  const navigateToReferenceNumeral = (direction: HighlightDirection) => {
    if (referenceNumeralMatchCount === 0) return

    let newIndex = currentReferenceNumeralIndex + direction
    if (newIndex < 0) newIndex = referenceNumeralMatchCount - 1
    if (newIndex >= referenceNumeralMatchCount) newIndex = 0

    setCurrentReferenceNumeralIndex(newIndex)

    setTimeout(() => {
      const matches = descriptionRef.current?.querySelectorAll<HTMLElement>('.reference-numeral-match')
      if (matches && matches[newIndex]) {
        matches[newIndex].scrollIntoView({ behavior: 'smooth', block: 'center' })
        matches.forEach((m, i) => {
          if (i === newIndex) {
            m.classList.add('ring-2', 'ring-purple-500')
          } else {
            m.classList.remove('ring-2', 'ring-purple-500')
          }
        })
      }
    }, 100)
  }

  // Check if this is a US patent/publication and generate download URL
  const getDownloadUrl = () => {
    if (!patentData?.patent_number) return null

    const docNumber = patentData.patent_number

    // Check if it's a US document (starts with US or is just digits)
    const isUS = /^US/.test(docNumber) || /^\d+$/.test(docNumber)
    if (!isUS) return null

    // Check if it's a publication (has kind code like A1, A2, etc.)
    const isPublication = /[A-Z]\d$/.test(docNumber)

    if (isPublication) {
      // For publications, remove country and kind codes
      return `https://image-ppubs.uspto.gov/dirsearch-public/print/downloadPdf/${removeCountryAndKindCodes(docNumber)}`
    } else {
      // For issued patents, use the document number as-is
      return `https://image-ppubs.uspto.gov/dirsearch-public/print/downloadPdf/${docNumber}`
    }
  }

  const downloadUrl = getDownloadUrl()

  // Helper function to download patent as DOCX
  const handleDownloadDocx = () => {
    if (!patentData) return

    try {
      let htmlContent = '<div>'

      // Add metadata if enabled
      if (includeMetadata) {
        htmlContent += '<div style="margin-bottom: 20px;">'
        htmlContent += `<p><strong>Patent Number:</strong> ${patentData.patent_number}</p>`
        if (patentData.inventors && patentData.inventors.length > 0) {
          htmlContent += `<p><strong>Inventors:</strong> ${patentData.inventors.join(', ')}</p>`
        }
        if (patentData.assignee) {
          htmlContent += `<p><strong>Assignee:</strong> ${patentData.assignee}</p>`
        }
        if (patentData.filing_date) {
          htmlContent += `<p><strong>Filing Date:</strong> ${new Date(patentData.filing_date).toLocaleDateString()}</p>`
        }
        if (patentData.publication_date) {
          htmlContent += `<p><strong>Publication Date:</strong> ${new Date(patentData.publication_date).toLocaleDateString()}</p>`
        }
        if (patentData.grant_date) {
          htmlContent += `<p><strong>Grant Date:</strong> ${new Date(patentData.grant_date).toLocaleDateString()}</p>`
        }
        htmlContent += '</div>'
      }

      // Add sections based on selected format
      if (exportFormat === 'option1') {
        // Option 1: Abstract, Description, Claims
        if (includeAbstract && patentData.abstract) {
          htmlContent += '<div><h2>Abstract</h2>'
          htmlContent += `<p>${patentData.abstract}</p></div>`
        }

        if (includeDescription && patentData.description) {
          htmlContent += '<div><h2>Description</h2>'
          htmlContent += patentData.description
          htmlContent += '</div>'
        }

        if (includeClaims && patentData.claims) {
          htmlContent += '<div><h2>Claims</h2>'
          htmlContent += `<div style="white-space: pre-wrap;">${patentData.claims}</div></div>`
        }
      } else {
        // Option 2: Description, Claims, Abstract
        if (includeDescription && patentData.description) {
          htmlContent += '<div><h2>Description</h2>'
          htmlContent += patentData.description
          htmlContent += '</div>'
        }

        if (includeClaims && patentData.claims) {
          htmlContent += '<div><h2>Claims</h2>'
          htmlContent += `<div style="white-space: pre-wrap;">${patentData.claims}</div></div>`
        }

        if (includeAbstract && patentData.abstract) {
          htmlContent += '<div><h2>Abstract</h2>'
          htmlContent += `<p>${patentData.abstract}</p></div>`
        }
      }

      htmlContent += '</div>'

      // Create full HTML document
      const title = patentData.title || patentData.patent_number
      const fullHtml = `
        <html>
          <head>
            <meta charset="UTF-8">
            <title>${title}</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                margin: 20px;
                line-height: 1.6;
              }
              h1 {
                color: #333;
                border-bottom: 2px solid #333;
                padding-bottom: 10px;
                margin-bottom: 20px;
              }
              h2 {
                color: #666;
                margin-top: 25px;
                margin-bottom: 15px;
              }
              p { margin: 10px 0; }
            </style>
          </head>
          <body>
            <h1>${title}</h1>
            ${htmlContent}
          </body>
        </html>
      `

      // Create and download the file
      const blob = new Blob(['\ufeff' + fullHtml], { type: 'application/msword;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${patentData.patent_number}_patent.doc`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error creating DOCX:', error)
    }
  }

  // Helper function to copy text to clipboard
  const copyToClipboard = async (
    text: string,
    setStateFn: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    try {
      // Strip HTML tags from text while preserving paragraph structure
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = text

      // Convert various block elements to paragraphs with double line breaks
      const blockElements = tempDiv.querySelectorAll<HTMLElement>('p, div, h1, h2, h3, h4, h5, h6, section, article, header')
      blockElements.forEach(elem => {
        const textContent = elem.textContent?.trim() ?? ''
        if (textContent) {
          elem.replaceWith(`${textContent}\n`)
        } else {
          elem.remove()
        }
      })

      const brElements = tempDiv.querySelectorAll<HTMLBRElement>('br')
      brElements.forEach(br => br.replaceWith('\n'))

      // Get the final text content
      let plainText = tempDiv.textContent || tempDiv.innerText || text

      // Remove any leading whitespace from each line
      plainText = plainText
        .split('\n')
        .map((line: string) => line.trimStart())
        .join('\n')

      // Remove excessive blank lines (more than 2 consecutive newlines)
      plainText = plainText.replace(/\n{3,}/g, '\n\n')

      // Trim the final result
      plainText = plainText.trim()

      await navigator.clipboard.writeText(plainText)
      setStateFn(true)
      setTimeout(() => setStateFn(false), 2000)
    } catch (err) {
      console.error('Failed to copy text:', err)
    }
  }

  if (loading) {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center ${theme === 'dark' ? 'bg-gray-900' : 'bg-primary-light'}`}>
        <div className="text-center max-w-2xl px-8">
          {/* Logo */}
          <div className="mb-8">
            <img
              src={logoImage}
              alt="Patent Loop"
              className="h-32 mx-auto mb-4"
            />
          </div>

          {/* Loader */}
          <div className="mb-8">
            <Loader />
          </div>

          {/* Tagline */}
          <div className={`mt-8 p-6 rounded-lg border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <h3 className={`text-xl font-bold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Want More Than Just Reading Patents?
            </h3>
            <p className={`text-base mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Sign up for Patent Loop to unlock powerful AI-driven patent analysis, automated IDS generation, office action responses, and collaborative drafting tools.
            </p>
            <a
              href="/"
              className="inline-block px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition"
            >
              Get Started Free
            </a>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return <PatentLandingPage initialError={error} failedPatentNumber={patentNumber} />
  }

  if (!patentData) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">Patent Not Found</h1>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            Return Home
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900 text-gray-200' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <div className={`sticky top-0 z-20 border-b ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
        <div className="max-w-7xl mx-auto px-2 wide:px-6 py-1">
          <div className="flex items-center gap-1 wide:gap-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center hover:opacity-80 transition flex-shrink-0"
            >
              <img
                src={logoImage}
                alt="Patent Loop"
                className="h-16"
              />
            </button>

            {/* Patent Selector Button */}
            <div className="relative flex-shrink-0">
              <button
                onClick={() => setShowPatentSelector(!showPatentSelector)}
                className={`px-2 wide:px-2 py-2 rounded-lg font-medium whitespace-nowrap ${theme === 'dark' ? 'bg-primary-light-4 text-primary hover:bg-primary-light' : 'bg-primary text-white hover:bg-primary-light'} transition`}
                title="Open different patent specification"
              >
                <span className="text-sm wide:text-base">📄 New Patent</span>
              </button>
              {showPatentSelector && (
                <div className={`absolute top-full mt-2 left-0 p-4 rounded-lg border shadow-lg z-30 min-w-[300px] ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                  <div className="mb-3">
                    <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                      Enter Patent Number
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., US10123456, EP1234567"
                      value={newPatentNumber}
                      onChange={(e) => setNewPatentNumber(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && newPatentNumber.trim()) {
                          navigate(`/${normalizePatentNumber(newPatentNumber)}`)
                          setShowPatentSelector(false)
                          setNewPatentNumber('')
                        }
                      }}
                      className={`w-full px-3 py-2 rounded-lg border ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      autoFocus
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        if (newPatentNumber.trim()) {
                          navigate(`/${normalizePatentNumber(newPatentNumber)}`)
                          setShowPatentSelector(false)
                          setNewPatentNumber('')
                        }
                      }}
                      disabled={!newPatentNumber.trim()}
                      className={`flex-1 px-3 py-2 rounded-lg font-medium ${
                        newPatentNumber.trim()
                          ? theme === 'dark' ? 'bg-blue-700 hover:bg-blue-600 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'
                          : theme === 'dark' ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      } transition`}
                    >
                      Open
                    </button>
                    <button
                      onClick={() => {
                        if (newPatentNumber.trim()) {
                          window.open(`/${normalizePatentNumber(newPatentNumber)}`, '_blank')
                          setShowPatentSelector(false)
                          setNewPatentNumber('')
                        }
                      }}
                      disabled={!newPatentNumber.trim()}
                      className={`flex-1 px-3 py-2 rounded-lg font-medium ${
                        newPatentNumber.trim()
                          ? theme === 'dark' ? 'bg-purple-700 hover:bg-purple-600 text-white' : 'bg-purple-600 hover:bg-purple-700 text-white'
                          : theme === 'dark' ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      } transition`}
                    >
                      New Tab
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Search Bar Container */}
            <div className={`flex-1 flex gap-1 wide:gap-2 items-center px-2 wide:px-3 py-1 rounded-lg ${theme === 'dark' ? 'bg-yellow-900/30 border border-yellow-700' : 'bg-yellow-50 border border-yellow-200'}`}>
              <input
                type="text"
                placeholder="Search patent spec..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`flex-1 px-2 wide:px-3 py-1.5 text-sm wide:text-base rounded border ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              />
              {searchQuery.trim() && searchMatchCount === 0 && (
                <div className={`flex items-center gap-1 px-2 py-1 rounded ${theme === 'dark' ? 'bg-red-700 text-gray-200' : 'bg-red-500 text-white'}`}>
                  <span className="text-xs wide:text-sm font-medium whitespace-nowrap">No results</span>
                </div>
              )}
              {searchMatchCount > 0 && (
                <>
                  <div className={`flex items-center gap-1 px-2 py-1 rounded ${theme === 'dark' ? 'bg-yellow-700 text-gray-200' : 'bg-yellow-200 text-gray-800'}`}>
                    <span className="text-xs wide:text-sm font-medium whitespace-nowrap">{currentMatchIndex + 1}<span className="hidden wide:inline"> / </span><span className="wide:hidden">/</span>{searchMatchCount}</span>
                    <button
                      onClick={() => navigateToMatch(-1)}
                      className={`px-1 py-0.5 rounded ${theme === 'dark' ? 'hover:bg-yellow-600' : 'hover:bg-yellow-300'}`}
                      title="Previous match"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => navigateToMatch(1)}
                      className={`px-1 py-0.5 rounded ${theme === 'dark' ? 'hover:bg-yellow-600' : 'hover:bg-yellow-300'}`}
                      title="Next match"
                    >
                      ↓
                    </button>
                  </div>
                  <label className={`flex items-center gap-1 px-2 py-1 rounded cursor-pointer ${theme === 'dark' ? 'bg-yellow-700 text-gray-200 hover:bg-yellow-600' : 'bg-yellow-200 text-gray-800 hover:bg-yellow-300'}`}>
                    <input
                      type="checkbox"
                      checked={filterSearchResults}
                      onChange={(e) => setFilterSearchResults(e.target.checked)}
                      className="cursor-pointer"
                    />
                    <span className="text-xs wide:text-sm whitespace-nowrap">Filter</span>
                  </label>
                </>
              )}
            </div>

            {/* Reference Numeral Container */}
            <div className={`flex gap-1 wide:gap-2 items-center flex-shrink-0 px-2 wide:px-3 py-1 rounded-lg ${theme === 'dark' ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
              <input
                type="text"
                placeholder="Search Ref# (100)"
                value={referenceNumeral}
                onChange={(e) => setReferenceNumeral(e.target.value)}
                className={`w-32 wide:w-[150px] px-2 wide:px-3 py-1.5 text-sm wide:text-base rounded border ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              />
              {referenceNumeral.trim() && referenceNumeralMatchCount === 0 && (
                <div className={`flex items-center gap-1 px-2 py-1 rounded ${theme === 'dark' ? 'bg-red-700 text-gray-200' : 'bg-red-500 text-white'}`}>
                  <span className="text-xs wide:text-sm font-medium whitespace-nowrap">No results</span>
                </div>
              )}
              {referenceNumeralMatchCount > 0 && (
                <>
                  <div className={`flex items-center gap-1 px-2 py-1 rounded ${theme === 'dark' ? 'bg-blue-700 text-gray-200' : 'bg-blue-200 text-gray-800'}`}>
                    <span className="text-xs wide:text-sm font-medium whitespace-nowrap">{currentReferenceNumeralIndex + 1}<span className="hidden wide:inline"> / </span><span className="wide:hidden">/</span>{referenceNumeralMatchCount}</span>
                    <button
                      onClick={() => navigateToReferenceNumeral(-1)}
                      className={`px-1 py-0.5 rounded ${theme === 'dark' ? 'hover:bg-blue-600' : 'hover:bg-blue-300'}`}
                      title="Previous reference numeral"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => navigateToReferenceNumeral(1)}
                      className={`px-1 py-0.5 rounded ${theme === 'dark' ? 'hover:bg-blue-600' : 'hover:bg-blue-300'}`}
                      title="Next reference numeral"
                    >
                      ↓
                    </button>
                  </div>
                  <label className={`flex items-center gap-1 px-2 py-1 rounded cursor-pointer ${theme === 'dark' ? 'bg-blue-700 text-gray-200 hover:bg-blue-600' : 'bg-blue-200 text-gray-800 hover:bg-blue-300'}`}>
                    <input
                      type="checkbox"
                      checked={filterReferenceNumeralResults}
                      onChange={(e) => setFilterReferenceNumeralResults(e.target.checked)}
                      className="cursor-pointer"
                    />
                    <span className="text-xs wide:text-sm whitespace-nowrap">Filter</span>
                  </label>
                </>
              )}
            </div>
            {patentData.claims && (
              <button
                onClick={() => setClaimsPinned(!claimsPinned)}
                title="Pin claims to the right"
                className={`px-2 wide:px-4 py-2 rounded-lg font-medium flex-shrink-0 ${claimsPinned ? 'bg-blue-600 text-white' : theme === 'dark' ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-900'}`}
              >
                <span className="hidden wide:inline">{claimsPinned ? '📌 Claims' : 'Pin Claims'}</span>
                <span className="wide:hidden">📌</span>
              </button>
            )}
            {patentData.drawings && patentData.drawings.length > 0 && (
              <button
                onClick={() => setDrawingsPinned(!drawingsPinned)}
                title="Pin drawings to the right"
                className={`px-2 wide:px-4 py-2 rounded-lg font-medium flex-shrink-0 ${drawingsPinned ? 'bg-purple-600 text-white' : theme === 'dark' ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-900'}`}
              >
                <span className="hidden wide:inline">{drawingsPinned ? '📌 Drawings' : 'Pin Drawings'}</span>
                <span className="wide:hidden">🖼️</span>
              </button>
            )}
            {downloadUrl && (
              <a
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`px-2 wide:px-4 py-2 rounded-lg font-medium flex-shrink-0 ${theme === 'dark' ? 'bg-green-700 text-white hover:bg-green-600' : 'bg-green-600 text-white hover:bg-green-700'} transition`}
                title="Download PDF"
              >
                <span className="hidden wide:inline">📥 Download PDF</span>
                <span className="wide:hidden">📥</span>
              </a>
            )}
            <div className="relative">
              <button
                onClick={async () => {
                  const currentUrl = window.location.href

                  // Check if Web Share API is available (mobile devices)
                  if (navigator.share) {
                    try {
                      await navigator.share({
                        title: patentData.title || 'Patent Document',
                        text: `Check out this patent: ${patentData.title || patentData.patent_number}`,
                        url: currentUrl
                      })
                    } catch (err) {
                      // User cancelled or error occurred
                      console.log('Share cancelled or failed:', err)
                    }
                  } else {
                    // Fallback: Copy to clipboard
                    try {
                      await navigator.clipboard.writeText(currentUrl)
                      setShowShareTooltip(true)
                      setTimeout(() => setShowShareTooltip(false), 2000)
                    } catch (err) {
                      console.error('Failed to copy URL:', err)
                    }
                  }
                }}
                className={`px-2 wide:px-2 py-2 wide:min-w-[86px] rounded-lg font-medium flex-shrink-0 ${theme === 'dark' ? 'bg-blue-700 text-white hover:bg-blue-600' : 'bg-blue-600 text-white hover:bg-blue-700'} transition`}
                title="Share this page"
              >
                <span className="hidden wide:inline">🔗 Share</span>
                <span className="wide:hidden">🔗</span>
              </button>
              {showShareTooltip && (
                <div className={`absolute top-full mt-2 left-1/2 transform -translate-x-1/2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${theme === 'dark' ? 'bg-gray-700 text-white' : 'bg-gray-800 text-white'} shadow-lg z-10`}>
                  Link copied to clipboard!
                </div>
              )}
            </div>
            {isLoggedIn ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className={`px-2 wide:px-4 py-2 rounded-lg font-semibold flex-shrink-0 flex items-center cursor-pointer hover:opacity-80 transition ${theme === 'dark' ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-900'}`}>
                    <span className="hidden wide:inline text-sm">👤 {username}</span>
                    <span className="wide:hidden">👤</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-white">
                  {/* User Info */}
                  <div className="px-3 py-3">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white font-bold text-lg mr-3">
                        {username.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-900 truncate">
                          {username}
                        </div>
                      </div>
                    </div>
                  </div>

                  <DropdownMenuSeparator className="bg-gray-200" />

                  {/* Account Options */}
                  <DropdownMenuItem className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                    <User className="w-4 h-4 mr-2" />
                    Profile
                  </DropdownMenuItem>

                  <DropdownMenuSeparator className="bg-gray-200" />

                  <DropdownMenuItem onClick={handleLogout} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                    <LogOut className="w-4 h-4 mr-2" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <a
                href="/"
                className="px-2 wide:px-2 py-2 wide:py-[1px] min-h-[40px] rounded-lg items-center font-semibold flex-shrink-0 bg-blue-600 text-white hover:bg-blue-700 transition wide:flex wide:flex-col wide:items-center"
              >
                <span className="wide:text-base text-xs">Sign Up<span className="hidden wide:inline"> Free</span></span>
                <span className="text-xs font-normal opacity-90 hidden wide:inline">No Credit Card Required</span>
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={`px-2 wide:px-6 py-1 wide:max-w-[calc(100vw-160px)] m-auto ${claimsPinned || drawingsPinned ? 'flex gap-2' : ''}`}>
        <div className={claimsPinned || drawingsPinned ? 'flex-1 min-w-0' : 'w-full'}>
        {/* Patent Header */}
        <div className="mb-8">
          <div className={`font-medium my-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
            {patentData.patent_number}
          </div>
          <div className="flex items-center gap-3 mb-4">
            <h1 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {patentData.title || 'Untitled Patent'}
            </h1>
            <div className="relative">
              <button
                onClick={() => copyToClipboard(patentData.title || 'Untitled Patent', setCopiedTitle)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                  theme === 'dark'
                    ? 'bg-gray-700 hover:bg-gray-600 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                }`}
                title="Copy title to clipboard"
              >
                📋 Copy
              </button>
              {copiedTitle && (
                <div className={`absolute top-full mt-2 left-1/2 transform -translate-x-1/2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                  theme === 'dark' ? 'bg-gray-700 text-white' : 'bg-gray-800 text-white'
                } shadow-lg z-10`}>
                  Copied!
                </div>
              )}
            </div>
            <button
              onClick={() => setShowExportModal(true)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                theme === 'dark'
                  ? 'bg-purple-700 hover:bg-purple-600 text-white'
                  : 'bg-purple-600 hover:bg-purple-700 text-white'
              }`}
              title="Download as Word document"
            >
              📄 Download DOC
            </button>
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {patentData.inventors && patentData.inventors.length > 0 && (
              <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className={`text-xs font-semibold mb-2 uppercase ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  Inventors
                </div>
                <div className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                  {patentData.inventors.join(', ')}
                </div>
              </div>
            )}

            {patentData.assignee && (
              <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className={`text-xs font-semibold mb-2 uppercase ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  Assignee
                </div>
                <div className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                  {patentData.assignee}
                </div>
              </div>
            )}

            {patentData.filing_date && (
              <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className={`text-xs font-semibold mb-2 uppercase ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  Filing Date
                </div>
                <div className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                  {new Date(patentData.filing_date).toLocaleDateString()}
                </div>
              </div>
            )}

            {patentData.publication_date && (
              <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className={`text-xs font-semibold mb-2 uppercase ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  Publication Date
                </div>
                <div className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                  {new Date(patentData.publication_date).toLocaleDateString()}
                </div>
              </div>
            )}

            {patentData.grant_date && (
              <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className={`text-xs font-semibold mb-2 uppercase ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  Grant Date
                </div>
                <div className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                  {new Date(patentData.grant_date).toLocaleDateString()}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Abstract */}
        {patentData.abstract && (() => {
          // Check if abstract should be shown when claims filter is active
          const shouldShowAbstract = !claimsFilterEnabled || !claimsFilterText ||
            patentData.abstract.toLowerCase().includes(claimsFilterText.toLowerCase())

          if (!shouldShowAbstract) return null

          return (
            <section className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Abstract
                </h2>
                <div className="relative">
                  <button
                    onClick={() => copyToClipboard(patentData.abstract, setCopiedAbstract)}
                    className={`px-2 py-1 rounded-lg text-xs font-medium transition ${
                      theme === 'dark'
                        ? 'bg-gray-700 hover:bg-gray-600 text-white'
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                    }`}
                    title="Copy abstract to clipboard"
                  >
                    📋 Copy
                  </button>
                  {copiedAbstract && (
                    <div className={`absolute top-full mt-2 left-1/2 transform -translate-x-1/2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                      theme === 'dark' ? 'bg-gray-700 text-white' : 'bg-gray-800 text-white'
                    } shadow-lg z-10`}>
                      Copied!
                    </div>
                  )}
                </div>
                {debouncedSearchQuery && (
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    filterSearchResults
                      ? theme === 'dark' ? 'bg-yellow-700 text-white' : 'bg-yellow-600 text-white'
                      : theme === 'dark' ? 'bg-yellow-900/50 text-gray-300' : 'bg-yellow-200 text-gray-800'
                  }`}>
                    {filterSearchResults ? 'Filtering' : 'Highlighting'}: "{debouncedSearchQuery.length > 20 ? debouncedSearchQuery.substring(0, 20) + '...' : debouncedSearchQuery}"
                  </span>
                )}
                {debouncedReferenceNumeral && (
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    filterReferenceNumeralResults
                      ? theme === 'dark' ? 'bg-blue-700 text-white' : 'bg-blue-600 text-white'
                      : theme === 'dark' ? 'bg-blue-900/50 text-gray-300' : 'bg-blue-200 text-gray-800'
                  }`}>
                    {filterReferenceNumeralResults ? 'Filtering' : 'Highlighting'}: "{debouncedReferenceNumeral}"
                  </span>
                )}
                {debouncedClaimsFilterText && (
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    claimsFilterEnabled
                      ? theme === 'dark' ? 'bg-green-700 text-white' : 'bg-green-600 text-white'
                      : theme === 'dark' ? 'bg-green-900/50 text-gray-300' : 'bg-green-200 text-gray-800'
                  }`}>
                    {claimsFilterEnabled ? 'Filtering' : 'Highlighting'}: "{debouncedClaimsFilterText.length > 20 ? debouncedClaimsFilterText.substring(0, 20) + '...' : debouncedClaimsFilterText}"
                  </span>
                )}
              </div>
              <div className={`p-6 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                <div
                  className={`text-base leading-relaxed ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}
                  dangerouslySetInnerHTML={{ __html: processedAbstract }}
                />
              </div>
            </section>
          )
        })()}

        {/* Claims - Only show if not pinned */}
        {patentData.claims && !claimsPinned && (
          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Claims
              </h2>
              <div className="relative">
                <button
                  onClick={() => copyToClipboard(patentData.claims, setCopiedClaims)}
                  className={`px-2 py-1 rounded-lg text-xs font-medium transition ${
                    theme === 'dark'
                      ? 'bg-gray-700 hover:bg-gray-600 text-white'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                  }`}
                  title="Copy claims to clipboard"
                >
                  📋 Copy
                </button>
                {copiedClaims && (
                  <div className={`absolute top-full mt-2 left-1/2 transform -translate-x-1/2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                    theme === 'dark' ? 'bg-gray-700 text-white' : 'bg-gray-800 text-white'
                  } shadow-lg z-10`}>
                    Copied!
                  </div>
                )}
              </div>
            </div>
            <div className={`p-6 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className={`space-y-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                {splitClaimsText(patentData.claims || '').map((claim: string, idx: number) => {
                  const claimMatch = claim.match(/^(\d+)\.\s*(.+)/s)
                  if (!claimMatch) return null

                  const claimNumber = claimMatch[1]
                  const claimText = claimMatch[2].trim()

                  // Check if this is a dependent claim - look for patterns like:
                  // "according to claim X", "of claim X", "claims X to Y", "any of claims"
                  const isDependentClaim = /(?:according\s+to|of)\s+(?:any\s+of\s+)?claims?\s+\d+/i.test(claimText)

                  // Format claim dependencies as links/highlights
                  // Handle both single claims and ranges (e.g., "claims 3 to 11")
                  let formattedText = claimText
                      .replace(
                        /claims?\s+(\d+)\s+(?:to|or)\s+(\d+)/gi,
                        (_match, num1: string, num2: string) => `<span class="${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'} font-semibold">claims ${num1} to ${num2}</span>`
                      )
                      .replace(
                        /claim\s+(\d+)/gi,
                        (_match, num: string) => `<span class="${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'} font-semibold">claim ${num}</span>`
                      )

                  // Add search query highlighting
                  const query = searchQuery.trim()
                  if (query && claimText.toLowerCase().includes(query.toLowerCase())) {
                    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                    const regex = new RegExp(`(${escapedQuery})`, 'gi')
                    formattedText = formattedText.replace(regex, '<mark class="bg-yellow-300 text-black search-match">$1</mark>')
                  }

                  return (
                    <div
                      key={idx}
                      className={`p-4 rounded-lg border-l-4 ${
                        isDependentClaim
                          ? theme === 'dark' ? 'border-blue-600 bg-gray-700/50' : 'border-blue-400 bg-blue-50/50'
                          : theme === 'dark' ? 'border-green-600 bg-gray-700' : 'border-green-500 bg-green-50'
                      }`}
                    >
                      <div className="flex gap-4">
                        <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center font-bold text-lg ${
                          isDependentClaim
                            ? theme === 'dark' ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-700'
                            : theme === 'dark' ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-700'
                        }`}>
                          {claimNumber}
                        </div>
                        <div className="flex-1">
                          <div
                            className="text-base leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: formattedText }}
                          />
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </section>
        )}

        {/* Description */}
        {patentData.description && (
          <section className="mb-8" ref={descriptionRef}>
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Description
              </h2>
              <div className="relative">
                <button
                  onClick={() => copyToClipboard(patentData.description, setCopiedDescription)}
                  className={`px-2 py-1 rounded-lg text-xs font-medium transition ${
                    theme === 'dark'
                      ? 'bg-gray-700 hover:bg-gray-600 text-white'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                  }`}
                  title="Copy description to clipboard"
                >
                  📋 Copy
                </button>
                {copiedDescription && (
                  <div className={`absolute top-full mt-2 left-1/2 transform -translate-x-1/2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                    theme === 'dark' ? 'bg-gray-700 text-white' : 'bg-gray-800 text-white'
                  } shadow-lg z-10`}>
                    Copied!
                  </div>
                )}
              </div>
              {debouncedSearchQuery && (
                <span className={`px-2 py-1 text-xs rounded-full ${
                  filterSearchResults
                    ? theme === 'dark' ? 'bg-yellow-700 text-white' : 'bg-yellow-600 text-white'
                    : theme === 'dark' ? 'bg-yellow-900/50 text-gray-300' : 'bg-yellow-200 text-gray-800'
                }`}>
                  {filterSearchResults ? 'Filtering' : 'Highlighting'}: "{debouncedSearchQuery.length > 20 ? debouncedSearchQuery.substring(0, 20) + '...' : debouncedSearchQuery}"
                </span>
              )}
              {debouncedReferenceNumeral && (
                <span className={`px-2 py-1 text-xs rounded-full ${
                  filterReferenceNumeralResults
                    ? theme === 'dark' ? 'bg-blue-700 text-white' : 'bg-blue-600 text-white'
                    : theme === 'dark' ? 'bg-blue-900/50 text-gray-300' : 'bg-blue-200 text-gray-800'
                }`}>
                  {filterReferenceNumeralResults ? 'Filtering' : 'Highlighting'}: "{debouncedReferenceNumeral}"
                </span>
              )}
              {debouncedClaimsFilterText && (
                <span className={`px-2 py-1 text-xs rounded-full ${
                  claimsFilterEnabled
                    ? theme === 'dark' ? 'bg-green-700 text-white' : 'bg-green-600 text-white'
                    : theme === 'dark' ? 'bg-green-900/50 text-gray-300' : 'bg-green-200 text-gray-800'
                }`}>
                  {claimsFilterEnabled ? 'Filtering' : 'Highlighting'}: "{debouncedClaimsFilterText.length > 20 ? debouncedClaimsFilterText.substring(0, 20) + '...' : debouncedClaimsFilterText}"
                </span>
              )}
            </div>
            <div className={`p-6 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              <div
                className={`text-base leading-relaxed ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} patent-description`}
                dangerouslySetInnerHTML={{
                  __html: processedDescription
                }}
              />
            </div>
          </section>
        )}
        </div>

        {/* Pinned Drawings Sidebar */}
        {drawingsPinned && patentData.drawings && patentData.drawings.length > 0 && (() => {
          const drawings = patentData.drawings ?? []
          return (
          <div
            ref={drawingViewerRef}
            className="flex-shrink-0 min-w-0 relative"
            style={{
              width: selectedDrawingIndex !== null
                ? `${Math.max(drawingViewerWidth, window.innerWidth * 0.4)}px`
                : `${drawingViewerWidth}px`,
              maxWidth: '60vw'
            }}
          >
            {/* Resize handle - left edge */}
            <div
              className={`absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize z-10 hover:bg-blue-500 ${isResizingDrawingWidth ? 'bg-blue-500' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault()
                setIsResizingDrawingWidth(true)
              }}
              title="Drag to resize width"
            />

            <div
              className={`sticky top-20 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} overflow-hidden relative`}
            >

              <div className={`px-4 py-2 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between mb-2">
                  <h2 className={`text-lg font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {selectedDrawingIndex !== null ? (
                      <>
                        {(() => {
                          const drawingSrc = drawings[selectedDrawingIndex]
                          const sheetMatch = drawingSrc.match(/-D(\d+)\.png/)
                          const sheetNumber = sheetMatch ? parseInt(sheetMatch[1]) : selectedDrawingIndex + 1
                          return sheetNumber === 0 ? 'Cover Figure' : `Sheet ${sheetNumber}`
                        })()}
                        <span className="text-xs ml-2 font-normal text-gray-500">Drag left border to resize</span>
                      </>
                    ) : (
                      `Drawings (${drawings.length - 1} Sheets)`
                    )}
                  </h2>
                  {selectedDrawingIndex !== null && (
                    <button
                      onClick={() => setSelectedDrawingIndex(null)}
                      className={`px-2 py-1 text-xs rounded ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-900'} transition`}
                    >
                      ← All
                    </button>
                  )}
                </div>
                {selectedDrawingIndex !== null && (
                  <div className="flex items-center gap-3 justify-center">
                    {/* Zoom controls */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setDrawingZoom((prev) => Math.max(prev - 25, 25))}
                        className={`p-1.5 rounded transition-colors ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-900'}`}
                        title="Zoom Out"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setDrawingZoom(100)}
                        className={`px-2 py-1 text-xs rounded transition-colors min-w-[45px] ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-900'}`}
                        title="Reset Zoom"
                      >
                        {drawingZoom}%
                      </button>
                      <button
                        onClick={() => setDrawingZoom((prev) => Math.min(prev + 25, 200))}
                        className={`p-1.5 rounded transition-colors ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-900'}`}
                        title="Zoom In"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    </div>

                    {/* Rotation controls */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setDrawingRotation((prev) => (prev - 90) % 360)}
                        className={`p-1.5 rounded transition-colors ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-900'}`}
                        title="Rotate Left 90°"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setDrawingRotation(0)}
                        className={`px-2 py-1 text-xs rounded transition-colors min-w-[35px] ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-900'}`}
                        title="Reset Rotation"
                      >
                        {drawingRotation}°
                      </button>
                      <button
                        onClick={() => setDrawingRotation((prev) => (prev + 90) % 360)}
                        className={`p-1.5 rounded transition-colors ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-900'}`}
                        title="Rotate Right 90°"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10H11a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="overflow-y-auto max-h-[calc(100vh-150px)] p-4">
                {selectedDrawingIndex !== null ? (
                  // Show only the selected drawing
                  <div className="space-y-3">
                    <button
                      onClick={() => window.open(drawings[selectedDrawingIndex], '_blank')}
                      className={`w-full px-3 py-2 text-sm font-medium rounded ${theme === 'dark' ? 'bg-blue-700 hover:bg-blue-600 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'} transition`}
                    >
                      🔗 Open in New Tab
                    </button>
                    <div
                      className="flex items-center justify-center"
                      style={{
                        minHeight: 'auto',
                        overflow: drawingZoom > 100 || drawingRotation % 180 !== 0 ? 'auto' : 'hidden',
                        position: 'relative',
                        padding: drawingRotation % 180 !== 0 ? '20px' : '0'
                      }}
                    >
                      <img
                        ref={(el) => {
                          if (el && el !== drawingImageRef) {
                            setDrawingImageRef(el)
                          }
                        }}
                        src={drawings[selectedDrawingIndex]}
                        alt={(() => {
                          const drawingSrc = drawings[selectedDrawingIndex]
                          const sheetMatch = drawingSrc.match(/-D(\d+)\.png/)
                          const sheetNumber = sheetMatch ? parseInt(sheetMatch[1]) : selectedDrawingIndex + 1
                          return sheetNumber === 0 ? 'Cover Figure' : `Sheet ${sheetNumber}`
                        })()}
                        onLoad={() => {
                          // Trigger recalculation when image loads
                          setDrawingImageRef((prev) => prev)
                        }}
                        className="rounded transition-transform duration-200"
                        style={{
                          imageRendering: 'crisp-edges',
                          transform: `rotate(${drawingRotation}deg) scale(${drawingZoom / 100})`,
                          maxWidth: '100%',
                          width: '100%',
                          height: 'auto',
                          display: 'block',
                          transformOrigin: 'center center'
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  // Show all drawings as thumbnails
                  <div className="space-y-4">
                    {drawings.map((drawingSrc, idx) => {
                      const sheetMatch = drawingSrc.match(/-D(\d+)\.png/)
                      const sheetNumber = sheetMatch ? parseInt(sheetMatch[1]) : idx + 1
                      const sheetLabel = sheetNumber === 0 ? 'Cover Figure' : `Sheet ${sheetNumber}`

                      return (
                        <div
                          key={idx}
                          className={`p-2 rounded-lg border cursor-pointer transition ${
                            theme === 'dark'
                              ? 'border-gray-700 bg-gray-700/30 hover:bg-gray-700/50 hover:border-gray-600'
                              : 'border-gray-200 bg-gray-50 hover:bg-gray-100 hover:border-gray-300'
                          }`}
                          onClick={() => setSelectedDrawingIndex(idx)}
                        >
                          <div className={`text-xs font-semibold mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            {sheetLabel}
                          </div>
                          <img
                            src={drawingSrc}
                            alt={sheetLabel}
                            className="w-full h-auto"
                            style={{ imageRendering: 'crisp-edges' }}
                          />
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )})()}

        {/* Pinned Claims Sidebar */}
        {claimsPinned && patentData.claims && (
          <div className="w-80 flex-shrink-0 min-w-0" ref={claimsSidebarRef}>
            <div className={`sticky top-20 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} overflow-hidden`}>
              <div className={`px-4 py-2 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                <h2 className={`text-lg font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Claims
                </h2>
                <div className="space-y-2">
                  <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} px-2`}>
                    Select text from claims to highlight in abstract/description
                  </div>
                  <div className="flex items-center gap-2">
                    <label className={`flex items-center gap-2 cursor-pointer p-2 rounded flex-1 ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                      <input
                        type="checkbox"
                        checked={claimsFilterEnabled}
                        onChange={(e) => setClaimsFilterEnabled(e.target.checked)}
                        className="cursor-pointer w-4 h-4"
                      />
                      <div className="flex-1">
                        <div className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                          Filter by selected text
                        </div>
                        {claimsFilterText && (
                          <div className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            "{claimsFilterText.length > 30 ? claimsFilterText.substring(0, 30) + '...' : claimsFilterText}"
                          </div>
                        )}
                      </div>
                    </label>
                    {claimsFilterText && (
                      <button
                        onClick={() => {
                          setClaimsFilterText('')
                          setClaimsFilterEnabled(false)
                        }}
                        className={`px-2 py-1 text-xs rounded whitespace-nowrap ${theme === 'dark' ? 'bg-red-700 hover:bg-red-600 text-white' : 'bg-red-600 hover:bg-red-700 text-white'} transition-colors`}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="overflow-y-auto max-h-[calc(100vh-232px)] p-4">
                <div className={`space-y-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  {splitClaimsText(patentData.claims || '').map((claim: string, idx: number) => {
                    const claimMatch = claim.match(/^(\d+)\.\s*(.+)/s)
                    if (!claimMatch) return null

                    const claimNumber = claimMatch[1]
                    const claimText = claimMatch[2].trim()
                    // Check if this is a dependent claim
                    const isDependentClaim = /(?:according\s+to|of)\s+(?:any\s+of\s+)?claims?\s+\d+/i.test(claimText)

                    // Format claim dependencies - handle ranges and single claims
                    let formattedText = claimText
                      .replace(
                        /claims?\s+(\d+)\s+(?:to|or)\s+(\d+)/gi,
                        (_match, num1: string, num2: string) => `<span class="${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'} font-semibold">claims ${num1} to ${num2}</span>`
                      )
                      .replace(
                        /claim\s+(\d+)/gi,
                        (_match, num: string) => `<span class="${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'} font-semibold">claim ${num}</span>`
                      )

                    // Add search query highlighting
                    const query = searchQuery.trim()
                    if (query && claimText.toLowerCase().includes(query.toLowerCase())) {
                      const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                      const regex = new RegExp(`(${escapedQuery})`, 'gi')
                      formattedText = formattedText.replace(regex, '<mark class="bg-yellow-300 text-black search-match">$1</mark>')
                    }

                    // Highlight selected text within claims
                    if (claimsFilterText) {
                      const escapedFilter = claimsFilterText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                      const regex = new RegExp(`(${escapedFilter})`, 'gi')
                      formattedText = formattedText.replace(regex, '<mark class="bg-green-300 text-black claims-filter-match">$1</mark>')
                    }

                    return (
                      <div
                        key={idx}
                        className={`p-3 rounded-lg border-l-4 ${
                          isDependentClaim
                            ? theme === 'dark' ? 'border-blue-600 bg-gray-700/50' : 'border-blue-400 bg-blue-50/50'
                            : theme === 'dark' ? 'border-green-600 bg-gray-700' : 'border-green-500 bg-green-50'
                        }`}
                      >
                        <div className="flex gap-3">
                          <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm ${
                            isDependentClaim
                              ? theme === 'dark' ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-700'
                              : theme === 'dark' ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-700'
                          }`}>
                            {claimNumber}
                          </div>
                          <div className="flex-1 select-text cursor-text">
                            <div
                              className="text-sm leading-relaxed"
                              dangerouslySetInnerHTML={{ __html: formattedText }}
                            />
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Export Options Modal */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className={`relative w-full max-w-md mx-4 rounded-lg shadow-xl ${
            theme === 'dark' ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
          }`}>
            {/* Modal Header */}
            <div className={`px-6 py-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className="text-lg font-semibold">Export Options</h3>
            </div>

            {/* Modal Content */}
            <div className="px-6 py-4 space-y-6">
              {/* Format Selection */}
              <div>
                <label className={`block text-sm font-medium mb-3 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Section Order
                </label>
                <div className="space-y-2">
                  <label className={`flex items-center p-3 rounded-lg border cursor-pointer transition ${
                    exportFormat === 'option1'
                      ? theme === 'dark'
                        ? 'bg-purple-900 border-purple-600'
                        : 'bg-purple-50 border-purple-500'
                      : theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 hover:bg-gray-650'
                        : 'bg-gray-50 border-gray-300 hover:bg-gray-100'
                  }`}>
                    <input
                      type="radio"
                      name="exportFormat"
                      value="option1"
                      checked={exportFormat === 'option1'}
                      onChange={(e) => setExportFormat(e.target.value)}
                      className="mr-3"
                    />
                    <span className="text-sm">Abstract → Description → Claims</span>
                  </label>
                  <label className={`flex items-center p-3 rounded-lg border cursor-pointer transition ${
                    exportFormat === 'option2'
                      ? theme === 'dark'
                        ? 'bg-purple-900 border-purple-600'
                        : 'bg-purple-50 border-purple-500'
                      : theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 hover:bg-gray-650'
                        : 'bg-gray-50 border-gray-300 hover:bg-gray-100'
                  }`}>
                    <input
                      type="radio"
                      name="exportFormat"
                      value="option2"
                      checked={exportFormat === 'option2'}
                      onChange={(e) => setExportFormat(e.target.value)}
                      className="mr-3"
                    />
                    <span className="text-sm">Description → Claims → Abstract</span>
                  </label>
                </div>
              </div>

              {/* Section Inclusion */}
              <div>
                <label className={`block text-sm font-medium mb-3 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Include Sections
                </label>
                <div className="space-y-2">
                  <label className={`flex items-center p-3 rounded-lg border cursor-pointer transition ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 hover:bg-gray-650'
                      : 'bg-gray-50 border-gray-300 hover:bg-gray-100'
                  }`}>
                    <input
                      type="checkbox"
                      checked={includeMetadata}
                      onChange={(e) => setIncludeMetadata(e.target.checked)}
                      className="mr-3"
                    />
                    <span className="text-sm">Patent Metadata</span>
                  </label>
                  <label className={`flex items-center p-3 rounded-lg border cursor-pointer transition ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 hover:bg-gray-650'
                      : 'bg-gray-50 border-gray-300 hover:bg-gray-100'
                  }`}>
                    <input
                      type="checkbox"
                      checked={includeAbstract}
                      onChange={(e) => setIncludeAbstract(e.target.checked)}
                      className="mr-3"
                    />
                    <span className="text-sm">Abstract</span>
                  </label>
                  <label className={`flex items-center p-3 rounded-lg border cursor-pointer transition ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 hover:bg-gray-650'
                      : 'bg-gray-50 border-gray-300 hover:bg-gray-100'
                  }`}>
                    <input
                      type="checkbox"
                      checked={includeDescription}
                      onChange={(e) => setIncludeDescription(e.target.checked)}
                      className="mr-3"
                    />
                    <span className="text-sm">Description</span>
                  </label>
                  <label className={`flex items-center p-3 rounded-lg border cursor-pointer transition ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 hover:bg-gray-650'
                      : 'bg-gray-50 border-gray-300 hover:bg-gray-100'
                  }`}>
                    <input
                      type="checkbox"
                      checked={includeClaims}
                      onChange={(e) => setIncludeClaims(e.target.checked)}
                      className="mr-3"
                    />
                    <span className="text-sm">Claims</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className={`px-6 py-4 border-t flex justify-end gap-3 ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <button
                onClick={() => setShowExportModal(false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  theme === 'dark'
                    ? 'bg-gray-700 hover:bg-gray-600 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleDownloadDocx()
                  setShowExportModal(false)
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  theme === 'dark'
                    ? 'bg-purple-700 hover:bg-purple-600 text-white'
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                }`}
              >
                📄 Export DOC
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PatentFullTextDisplay
